import { Box, Chip, Tooltip, Typography, IconButton, Avatar } from '@mui/material';
import { Datagrid, FunctionField, List, useRecordContext, useTranslate } from 'react-admin';
import { chains } from 'src/assets/data';
import { EmptyContent } from 'src/components/empty-content/empty-content';
import XCurrencyField from '../../components/XCurrencyField';
import XDateField from '../../components/XDateField';
import AddressField from '../../components/AddressField';
import { Iconify } from 'src/components/iconify/iconify';

const DexTradeHistory = ({ selectedWallet }: { selectedWallet: any[] }) => {
  const record = useRecordContext();
  const t = useTranslate();
  if (!record) return null;

  const filter =
    selectedWallet && selectedWallet.length > 0
      ? {
          walletAddresses: selectedWallet.map((w) => {
            const address = w.walletAddress;
            const chainId = w.chainId;
            const chainName = chains.find((c) => c.id === chainId);
            return { address, chainId, chainName };
          }),
        }
      : {};

  return (
    <List
      resource={`user-management/user-info/${record?.id}/dex-trade-history`}
      filter={filter}
      filterDefaultValues={filter}
      exporter={false}
      perPage={25}
      empty={<EmptyContent />}
      disableSyncWithLocation
    >
      <Datagrid
        bulkActionButtons={false}
        className="user-dex-trade-history"
        empty={<EmptyContent />}

        sx={{
          overflowX: 'scroll',
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        <FunctionField
          label={t('module.userInfo.coin')}
          render={(trade: any) => (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {trade.coin}
              </Typography>
            </Box>
          )}
        />

        <FunctionField
          label={t('module.userInfo.direction')}
          render={(trade: any) => {
            const directionMap: {
              [key: string]: { color: 'success' | 'error' | 'default'; label: string };
            } = {
              'Open Long': { color: 'success', label: 'Long' },
              'Close Long': { color: 'error', label: 'Close Long' },
              'Open Short': { color: 'error', label: 'Short' },
              'Close Short': { color: 'success', label: 'Close Short' },
            };

            const { color = 'default', label= 'Unknown' } = directionMap[trade.dir] || {};

            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label={label} color={color} size="small" />
              </Box>
            );
          }}
        />

        {/* Price */}
        <FunctionField
          label={t('module.userInfo.price')}
          render={(trade: any) => (
            <XCurrencyField
              currency={{ symbol: '$', position: 'before' }}
              value={parseFloat(trade.px) || 0}
            />
          )}
        />

        {/* Size */}
        <FunctionField
          label={t('module.userInfo.size')}
          render={(trade: any) => (
            <XCurrencyField
              value={parseFloat(trade.sz) || 0}
              currency={{ symbol: `(${trade.coin})`, position: 'after', space: true }}
            />
          )}
        />

        {/* Start Position */}
        <FunctionField
          label={t('module.userInfo.startPosition')}
          render={(trade: any) => (
            <XCurrencyField
              value={parseFloat(trade.startPosition) || 0}
              currency={{ symbol: `(${trade.coin})`, position: 'after', space: true }}
            />
          )}
        />

        {/* Fee */}
        <FunctionField
          label={t('module.userInfo.fee')}
          render={(trade: any) => (
            <XCurrencyField
              currency={{ symbol: '$', position: 'before' }}
              value={parseFloat(trade.fee) || 0}
            />
          )}
        />

        {/* Closed PnL */}
        <FunctionField
          label={t('module.userInfo.closedPnl')}
          render={(trade: any) => (
            <XCurrencyField
              currency={{ symbol: '$', position: 'before' }}
              colorize
              value={parseFloat(trade.closedPnl) || 0}
            />
          )}
        />

        {/* Time */}
        <FunctionField
          label={t('module.userInfo.time')}
          render={(trade: any) => {
            // Chuyển từ milliseconds sang ISO string
            const timestamp =
              typeof trade.time === 'number' ? new Date(trade.time).toISOString() : trade.time;
            return <XDateField relative value={timestamp} />;
          }}
        />

        {/* Hash */}
        <FunctionField
          label={t('module.userInfo.hash')}
          render={(trade: any) => (
            <Tooltip
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'inherit', wordBreak: 'break-all' }}>
                    {trade.hash}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(trade.hash)}
                    sx={{
                      color: 'inherit',
                      padding: '2px',
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    <Iconify icon="solar:copy-linear" width={14} />
                  </IconButton>
                </Box>
              }
              arrow
            >
              <AddressField address={trade.hash} />
            </Tooltip>
          )}
        />
      </Datagrid>
    </List>
  );
};

export default DexTradeHistory;
